import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Certificate } from 'app/library/certificate/models/certificate.model';
import { IEntityState } from 'app/projects/entity/src/lib/interfaces/entity.state.interface';
import { EntityState } from 'app/projects/entity/src/lib/models/entity.state';
import {
    AddCertificates,
    CreateCertificate,
    CreateCertificateFailure,
    CreateCertificateSuccess,
    DeleteCertificate,
    DeleteCertificateFailure,
    DeleteCertificateSuccess,
    FetchCertificate,
    FetchCertificateFailure,
    FetchCertificates,
    FetchCertificatesFailure,
    FetchCertificatesSuccess,
    FetchCertificateSuccess,
    SetCertificate,
    SetCertificates,
    UnsetCertificate,
    UpdateCertificate,
    UpdateCertificateFailure,
    UpdateCertificateSuccess,
    UploadCertificateTemplate,
    UploadCertificateTemplateFailure,
    UploadCertificateTemplateSuccess,
} from '../actions';
import { CertificateApiService } from '../services/certificate.api-service';
import { CertificateStateModel } from './certificate.state-model';

@State<CertificateStateModel>({
    name: 'certificate',
    defaults: new CertificateStateModel(),
})
@Injectable()
export class CertificateState extends EntityState<Certificate> implements IEntityState<Certificate> {
    @Selector()
    static getEntity(state: CertificateStateModel, id: string = state.item): Certificate {
        if (!state.map) {
            return null;
        }

        return state.map.get(id) || null;
    }

    @Selector()
    static getEntities(state: CertificateStateModel): Certificate[] {
        if (!state.map) {
            return null;
        }

        return Array.from(state.map.values());
    }

    constructor(private certificateApiService: CertificateApiService, private store: Store) {
        super(certificateApiService, 30);

        this._propstoSearch = Certificate.props_to_search;
    }

    @Action(AddCertificates)
    addEntities(ctx: StateContext<CertificateStateModel>, action: AddCertificates): void {
        this._addEntities(ctx, action);
    }

    @Action(SetCertificate)
    setEntity(ctx: StateContext<CertificateStateModel>, action: SetCertificate): void {
        this._setEntity(ctx, action, AddCertificates);
    }

    @Action(UnsetCertificate)
    unsetEntity(ctx: StateContext<CertificateStateModel>): void {
        this._unsetEntity(ctx);
    }

    @Action(CreateCertificate)
    createEntity(ctx: StateContext<CertificateStateModel>, action: CreateCertificate): void {
        this._createEntity(ctx, action, CreateCertificateSuccess, CreateCertificateFailure, AddCertificates).then(() => {});
    }

    @Action(UpdateCertificate)
    updateEntity(ctx: StateContext<CertificateStateModel>, action: UpdateCertificate): void {
        this._updateEntity(ctx, action, UpdateCertificateSuccess, UpdateCertificateFailure, AddCertificates).then(() => {});
    }

    @Action(DeleteCertificate)
    deleteEntity(ctx: StateContext<CertificateStateModel>, action: DeleteCertificate): void {
        this._deleteEntity(ctx, action, DeleteCertificateSuccess, DeleteCertificateFailure).then(() => {});
    }

    @Action(FetchCertificate)
    fetchEntity(ctx: StateContext<CertificateStateModel>, action: FetchCertificate): void {
        this._fetchEntity(ctx, action, FetchCertificateSuccess, FetchCertificateFailure, AddCertificates, SetCertificate).then(() => {});
    }

    @Action(FetchCertificateSuccess)
    fetchEntitySuccess(ctx: StateContext<CertificateStateModel>, action: FetchCertificateSuccess): void {
        this._fetchEntitySuccess(ctx, action);
    }

    @Action(FetchCertificateFailure)
    fetchEntityFailure(ctx: StateContext<CertificateStateModel>, action: FetchCertificateFailure): void {
        this._fetchEntityFailure(ctx, action);
    }

    @Action(SetCertificates)
    setEntities(ctx: StateContext<CertificateStateModel>, action: SetCertificates): void {
        this._setEntities(ctx, action);
    }

    @Action(FetchCertificates)
    fetchEntities(ctx: StateContext<CertificateStateModel>): void {
        throw new Error('Not implemented');
    }

    @Action(FetchCertificatesSuccess)
    fetchEntitiesSuccess(ctx: StateContext<CertificateStateModel>): void {
        throw new Error('Not implemented');
    }

    @Action(FetchCertificatesFailure)
    fetchEntitiesFailure(ctx: StateContext<CertificateStateModel>): void {
        throw new Error('Not implemented');
    }

    @Action(UploadCertificateTemplate)
    uploadEncryptionKey({ dispatch, getState, patchState }: StateContext<CertificateStateModel>, { file }: UploadCertificateTemplate): void {
        this.certificateApiService.uploadTemplate(file).subscribe(
            (certificate) => {
                const state = getState();

                const list = [...CertificateState.getEntities(state), certificate];

                dispatch([new SetCertificates(list), new UploadCertificateTemplateSuccess(certificate)]);
            },
            (response: HttpErrorResponse) => {
                dispatch(new UploadCertificateTemplateFailure());
            }
        );
    }
}
